// Carry-over incident reports — the office write-up for a parcel that can't just be
// carried over to tomorrow (lost / missing, stolen, damaged beyond delivery). Each
// incident goes into that day's carry-over email to Australia Post as its own card:
// consignment, article, receiver, what happened, action taken, and photos — the
// driver's own photos for the day (picked from what they sent) plus anything the
// office adds (a Track & Trace screenshot, the consignment page…).
//
// Stored in the settings KV per date (see _shared/carryover-incidents.js). Remove is
// soft with Restore: this is an AP-facing record of a lost parcel.
import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Camera, Plus, Trash2, RotateCcw, Pencil, X } from 'lucide-react';
import { uprightJpegBytes } from '../utils/imageOrient';

const TYPES = ['Missing / lost parcel', 'Stolen parcel', 'Damaged — not deliverable', 'Other incident'];
const FN = '/.netlify/functions/carryover-sb';
const call = (action, body) => fetch(FN, {
  method: 'POST', headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ action, ...body }),
}).then(r => r.json());

const EMPTY = { driver: '', name: '', type: TYPES[0], count: 1, consignment: '', articleId: '', receiver: '', sender: '', whatHappened: '', actionTaken: '', photos: [] };

const input = { padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.82rem', width: '100%', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };
const btn = (bg, fg, border) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 'var(--radius)', border: border || 'none', background: bg, color: fg, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' });

async function fileToDataUrl(file) {
  const bytes = await uprightJpegBytes(file, 2000, 0.85);
  const blob = bytes ? new Blob([bytes], { type: 'image/jpeg' }) : file;
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
}

export default function CarryOverIncidents({ date, drivers = [], nameOf = () => '', onChange }) {
  const [list, setList] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [form, setForm] = useState(null);           // EMPTY-shaped draft, or null
  const [driverPhotos, setDriverPhotos] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    if (!date) return;
    call('listIncidents', { date, includeDeleted: true }).then(d => {
      const all = (d && d.incidents) || [];
      setList(all);
      if (onChange) onChange(all.filter(i => !i.deletedAt));
    }).catch(() => {});
  }, [date, onChange]);
  useEffect(() => { load(); setForm(null); }, [load]);

  // The chosen driver's own photos for the day, to pick from.
  useEffect(() => {
    if (!form || !form.driver) { setDriverPhotos([]); return; }
    call('incidentDriverPhotos', { date, driver: form.driver })
      .then(d => setDriverPhotos((d && d.photos) || [])).catch(() => setDriverPhotos([]));
  }, [date, form && form.driver]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const picked = (path) => form && form.photos.some(p => p.path === path);
  function togglePhoto(p) {
    setForm(f => picked(p.path)
      ? { ...f, photos: f.photos.filter(x => x.path !== p.path) }
      : { ...f, photos: [...f.photos, { path: p.path, url: p.url, caption: p.note === 'End of shift' ? 'Driver — end of shift scanner' : 'Driver photo', source: 'driver' }] });
  }

  async function addFiles(files) {
    const imgs = [...(files || [])].filter(f => /^image\//.test(f.type));
    if (!imgs.length) return;
    setBusy('upload'); setMsg(null);
    try {
      for (const f of imgs) {
        const dataUrl = await fileToDataUrl(f);
        const d = await call('uploadIncidentPhoto', { date, dataUrl });
        if (!d.success) throw new Error(d.error || 'Upload failed');
        setForm(cur => ({ ...cur, photos: [...cur.photos, { path: d.path, url: d.url, caption: 'Office — supporting screenshot', source: 'office' }] }));
      }
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(''); }
  }

  // Paste a screenshot straight in (⌘V) while the form is open.
  useEffect(() => {
    if (!form) return undefined;
    const onPaste = (e) => {
      const files = [...(e.clipboardData && e.clipboardData.files || [])];
      if (files.length) { e.preventDefault(); addFiles(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [!!form]);

  async function save() {
    if (!form.driver) { setMsg({ ok: false, text: 'Choose the driver.' }); return; }
    if (!form.whatHappened.trim()) { setMsg({ ok: false, text: 'Say what happened — AP will read this.' }); return; }
    setBusy('save'); setMsg(null);
    try {
      const d = await call('saveIncident', { date, incident: { ...form, name: form.name || nameOf(form.driver) } });
      if (!d.success) throw new Error(d.error || 'Save failed');
      setForm(null); setMsg({ ok: true, text: 'Incident saved — it will go in this day\'s carry-over report.' });
      load();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(''); }
  }

  async function setRemoved(inc, removed) {
    if (removed && !window.confirm(`Remove the ${inc.type.toLowerCase()} report for ${inc.driver} from this report? You can restore it.`)) return;
    setBusy(inc.id);
    try { await call(removed ? 'removeIncident' : 'restoreIncident', { date, id: inc.id }); load(); }
    finally { setBusy(''); }
  }

  const live = list.filter(i => !i.deletedAt);
  const removed = list.filter(i => i.deletedAt);

  return (
    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px solid rgba(220,38,38,0.35)', background: 'rgba(220,38,38,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <AlertTriangle size={16} color="var(--hp-red)" />
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>Incident reports</span>
        <span style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
          Missing, stolen or damaged parcels. Each goes in the email to AP with its details and photos.
        </span>
        <span style={{ flex: 1 }} />
        {!form && (
          <button onClick={() => { setForm({ ...EMPTY }); setMsg(null); }} style={btn('var(--hp-red)', '#fff')}>
            <Plus size={14} /> Report a missing parcel
          </button>
        )}
      </div>

      {live.map(inc => (
        <div key={inc.id} style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderLeft: '4px solid var(--hp-red)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--hp-red)', fontSize: '0.85rem' }}>{inc.type}</strong>
            <strong style={{ fontSize: '0.85rem' }}>{inc.driver}</strong>
            {inc.source === 'driver' && !inc.checkedAt && (
              <span title="Reported by the driver in the portal. Open it with Edit, check the wording, add the receiver, then Save."
                style={{ fontSize: '0.68rem', fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: 'rgba(245,158,11,0.15)', color: 'var(--hp-amber)', border: '1px solid rgba(245,158,11,0.4)' }}>
                FROM DRIVER: CHECK BEFORE SENDING
              </span>
            )}
            <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              {inc.count} parcel{inc.count === 1 ? '' : 's'}{inc.consignment ? ` · ${inc.consignment}` : ''}{inc.articleId ? ` · ${inc.articleId}` : ''}
            </span>
            <span style={{ flex: 1 }} />
            <button onClick={() => { setForm({ ...EMPTY, ...inc }); setMsg(null); }} style={btn('none', 'var(--hp-gold)')}><Pencil size={13} /> Edit</button>
            <button onClick={() => setRemoved(inc, true)} disabled={busy === inc.id} style={btn('none', 'var(--hp-red)')}><Trash2 size={13} /> Remove</button>
          </div>
          {inc.receiver && <div style={{ fontSize: '0.8rem', marginTop: 4 }}><span style={{ color: 'var(--hp-dim)' }}>To:</span> {inc.receiver}</div>}
          <div style={{ fontSize: '0.8rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{inc.whatHappened}</div>
          {inc.actionTaken && <div style={{ fontSize: '0.8rem', marginTop: 4, whiteSpace: 'pre-wrap' }}><span style={{ color: 'var(--hp-dim)' }}>Action:</span> {inc.actionTaken}</div>}
          {inc.photos && inc.photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {inc.photos.map(p => (
                <a key={p.path} href={p.url} target="_blank" rel="noreferrer" title={p.caption}>
                  <img src={p.url} alt={p.caption} style={{ width: 72, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}

      {removed.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.76rem' }}>
          <button onClick={() => setShowRemoved(s => !s)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
            {showRemoved ? '▾' : '▸'} {removed.length} removed
          </button>
          {showRemoved && removed.map(inc => (
            <div key={inc.id} style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4, color: 'var(--hp-dim)' }}>
              <span style={{ textDecoration: 'line-through' }}>{inc.type} · {inc.driver}{inc.consignment ? ` · ${inc.consignment}` : ''}</span>
              <button onClick={() => setRemoved(inc, false)} disabled={busy === inc.id} style={btn('none', 'var(--hp-gold)')}><RotateCcw size={12} /> Restore</button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div style={{ marginTop: 12, padding: 14, borderRadius: 'var(--radius)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <div>
              <label style={lbl}>Driver</label>
              <input list="coi-drivers" value={form.driver} placeholder="Login e.g. BAGNATOA"
                onChange={e => set('driver', e.target.value.toUpperCase().trim())} style={input} />
              <datalist id="coi-drivers">{drivers.map(d => <option key={d} value={d}>{nameOf(d)}</option>)}</datalist>
            </div>
            <div>
              <label style={lbl}>Type</label>
              <select value={form.type} onChange={e => set('type', e.target.value)} style={input}>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Parcels</label>
              <input type="number" min="1" value={form.count} onChange={e => set('count', e.target.value)} style={input} />
            </div>
            <div>
              <label style={lbl}>Consignment</label>
              <input value={form.consignment} placeholder="e.g. KCE2046189" onChange={e => set('consignment', e.target.value.toUpperCase())} style={input} />
            </div>
            <div>
              <label style={lbl}>Article ID / barcode</label>
              <input value={form.articleId} placeholder="e.g. 00093318642071085241" onChange={e => set('articleId', e.target.value)} style={input} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginTop: 10 }}>
            <div>
              <label style={lbl}>Receiver (name + address)</label>
              <textarea rows={2} value={form.receiver} onChange={e => set('receiver', e.target.value)} style={input} />
            </div>
            <div>
              <label style={lbl}>Sender</label>
              <textarea rows={2} value={form.sender} onChange={e => set('sender', e.target.value)} style={input} />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>What happened</label>
            <textarea rows={3} value={form.whatHappened} onChange={e => set('whatHappened', e.target.value)} style={input}
              placeholder="What the driver reported, in plain words." />
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Action taken</label>
            <textarea rows={2} value={form.actionTaken} onChange={e => set('actionTaken', e.target.value)} style={input}
              placeholder="e.g. Driver returned and retraced his steps; parcel not found." />
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={lbl}>{form.driver ? `${form.driver}'s photos today: tap to include` : 'Driver photos: choose the driver first'}</label>
            {form.driver && driverPhotos.length === 0 && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>No photos from {form.driver} for this day.</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {driverPhotos.map(p => (
                <button key={p.path} onClick={() => togglePhoto(p)} title={p.note}
                  style={{ padding: 0, border: picked(p.path) ? '3px solid var(--hp-gold)' : '3px solid transparent', borderRadius: 8, background: 'none', cursor: 'pointer', position: 'relative' }}>
                  <img src={p.url} alt={p.note} style={{ width: 84, height: 112, objectFit: 'cover', borderRadius: 5, display: 'block', opacity: picked(p.path) ? 1 : 0.55 }} />
                  <span style={{ position: 'absolute', left: 4, bottom: 4, fontSize: '0.62rem', fontWeight: 700, background: 'rgba(0,0,0,0.65)', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>
                    {picked(p.path) ? '✓ ' : ''}{p.note}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={lbl}>Included photos: add screenshots (or paste with ⌘V)</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {form.photos.map((p, i) => (
                <div key={p.path} style={{ width: 120 }}>
                  <div style={{ position: 'relative' }}>
                    <img src={p.url} alt="" style={{ width: 120, height: 150, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)', display: 'block' }} />
                    <button onClick={() => setForm(f => ({ ...f, photos: f.photos.filter(x => x.path !== p.path) }))} title="Take out of this report"
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: 999, width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} /></button>
                  </div>
                  <input value={p.caption} placeholder={`Photo ${i + 1}`} onChange={e => setForm(f => ({ ...f, photos: f.photos.map(x => x.path === p.path ? { ...x, caption: e.target.value } : x) }))}
                    style={{ ...input, fontSize: '0.7rem', padding: '4px 6px', marginTop: 4 }} />
                </div>
              ))}
              <label style={{ ...btn('var(--hp-bg)', 'var(--hp-text)', '1px dashed var(--hp-border)'), height: 150, width: 120, flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
                <Camera size={18} />
                {busy === 'upload' ? 'Uploading…' : 'Add photo'}
                <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                  onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={save} disabled={!!busy} style={btn('var(--hp-gold)', 'var(--hp-black)')}>
              {busy === 'save' ? 'Saving…' : form.id ? '✓ Save changes' : '✓ Add to carry-over report'}
            </button>
            <button onClick={() => { setForm(null); setMsg(null); }} disabled={!!busy} style={btn('var(--hp-surface)', 'var(--hp-dim)', '1px solid var(--hp-border)')}>Cancel</button>
          </div>
        </div>
      )}

      {msg && <div style={{ marginTop: 8, fontSize: '0.78rem', fontWeight: 700, color: msg.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>{msg.ok ? '✓ ' : '✗ '}{msg.text}</div>}
    </div>
  );
}
